import { mapState, mapGetters } from 'vuex';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import { templateMixin } from './templateMixin';
import cloneDeep from 'lodash/cloneDeep';
import ApiService from '@/services/apiService';
const DEFAULT_ITEMS_PER_PAGE = 10;
const ITEMS_PER_PAGE_RANGE = [10, 20, 50, 100];
const FETCH_LIST_DEBOUNCE_TIME_MS = 300;
const DEFAULT_TABLE_PAGINATION_OPTIONS = {
  page: 1,
  itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
  sortBy: [],
  sortDesc: [],
  groupBy: [],
  groupDesc: [],
  multiSort: false,
  mustSort: false
};
export default {
  name: 'CoreListViewPage',
  mixins: [templateMixin],
  props: {
    /**
     * Function to fetch items
     * @type {Function}
     * @required
     * @param {Object} params (search: string, page: number, perPage: number, ...externalFetchParams)
     * @returns {Promise} (items: Array, total: number)
     */
    fetchItems: {
      type: Function,
      required: false,
      default: null
    },
    staticItems: {
      type: Array,
      default: () => [],
      required: false
    },
    externalFetchParams: {
      type: Object,
      default: () => ({})
    },
    /**
     * Function to delete item
     * @type {Function}
     * @required
     * @param {Object} item (item from fetchItems request)
     */
    deleteItem: {
      type: Function,
      required: false,
      default: null
    },
    /**
     * Snack message to show after successful delete
     * @type {String}
     */
    deleteItemSnackMessage: {
      type: String,
      required: false,
      default: ''
    },
    /**
     * Snack message to show after error on delete
     * @type {String}
     */
    deleteItemErrorSnackMessage: {
      type: [String, Function],
      required: false,
      default: ''
    },
    /**
     * Table headers
     * @type {Array} [{ text: String (col label), value: String (item property key), align?: String, sortable?: Boolean }]
     */
    tableHeaders: {
      type: Array,
      required: true
    },
    /**
     * identifier key for items, needs to be unique and needs to exist in all items
     * @type {String}
     * @default 'id'
     */
    tableItemKey: {
      type: String,
      default: 'id'
    },
    /**
     * should the core-header be shown, or is this a headless list?
     * if unset, the header-buttons obviously will also not be shown, and all the attributes be ignored.
     * @type {Boolean}
     * @default true
     */
    showHeader: {
      type: Boolean,
      default: true
    },
    /**
     * Header buttons
     * @type {Array} [{ dataCy?: string, color?: string, type?: string, tooltip?: string, href?: string, target?: string, icon?: string, action?: string }]
     */
    headerButtons: {
      type: Array,
      default: () => []
    },
    /**
     * Page headline
     * @type {String}
     */
    headline: {
      type: String,
      default: ''
    },
    /**
     * Page subtitle
     * @type {String}
     */
    subtitle: {
      type: String,
      default: ''
    },
    /**
     * Function to get the href for a row
     * @type {Function}
     * @param {Object} item
     * @returns {String}
     */
    rowHref: {
      type: Function,
      required: false,
      default: () => () => '#'
    },
    createButtonHref: {
      type: String,
      default: ''
    },
    hasSearchBar: {
      type: Boolean,
      default: true
    },
    fetchErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    showBetaBadge: {
      type: [Boolean, String],
      required: false,
      default: () => false
    },
    emptyStateIcon: {
      type: String,
      required: true
    },
    emptyStateTitle: {
      type: String,
      required: true
    },
    emptyStateDescription: {
      type: String,
      required: true
    },
    emptyStateButtonLabel: {
      type: String,
      required: false,
      default: ''
    },
    emptyStateButtonDisabled: {
      type: Boolean,
      required: false,
      default: false
    },
    hideEditingButtons: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * List type
     * @type {String}
     */
    listType: {
      type: String,
      default: ''
    }
  },
  data: function () {
    return {
      textSearchValue: '',
      showDeletePrompt: false,
      isFetching: false,
      isSettingInitialPagination: false,
      deleteItemError: null,
      itemToBeDeleted: null,
      items: [],
      totalItemsCount: null,
      tablePaginationOptions: {
        ...DEFAULT_TABLE_PAGINATION_OPTIONS
      }
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme', 'permissions']),
    ...mapGetters({
      sessionUserId: 'authentication/userId'
    }),
    hasCreateButton() {
      return typeof this.$listeners['button:create'] === 'function' || this.createButtonHref;
    },
    deletePromptTitle() {
      return this.$i18n_t('Core.ListViewPage.deleteItem.prompt.title');
    },
    deletePromptCancelButtonLabel() {
      return this.$i18n_t('Core.ListViewPage.deleteItem.prompt.cancelButton.label');
    },
    deletePromptDeleteButtonLabel() {
      return this.$i18n_t('Core.ListViewPage.deleteItem.prompt.deleteButton.label');
    },
    tableFooterProps() {
      return {
        itemsPerPageText: this.$i18n_t('Core.ListViewPage.tableFooter.itemsPerPageText'),
        itemsPerPageOptions: [...ITEMS_PER_PAGE_RANGE]
      };
    },
    completeTableHeaders() {
      const headers = cloneDeep(this.tableHeaders);

      // Determine if any buttons should be visible
      const hasEditButton = typeof this.$listeners['button:edit'] === 'function' && !this.hideEditingButtons;
      const hasDeleteButton = typeof this.deleteItem === 'function' && !this.hideEditingButtons;
      const actionHeaderButtons = [];
      if (hasEditButton) {
        actionHeaderButtons.push({
          dataCy: 'list_btn_edit',
          icon: 'fal fa-edit',
          visible: hasEditButton,
          onClick: item => this.$emit('button:edit', item)
        });
      }
      if (hasDeleteButton) {
        actionHeaderButtons.push({
          dataCy: 'list_btn_delete',
          icon: 'fal fa-trash-alt',
          visible: hasDeleteButton,
          onClick: item => this.onClickDeleteItem(item)
        });
      }
      if (actionHeaderButtons.length > 0) {
        const actionsHeader = {
          text: this.$i18n_t('Core.ListViewPage.table.headers.actions'),
          isButtons: true,
          width: '125px',
          align: 'center',
          sortable: false,
          buttons: actionHeaderButtons
        };
        headers.push(actionsHeader);
      }
      return headers;
    },
    combinedHeaderButtons() {
      const buttons = [...this.headerButtons];
      if (this.hasCreateButton !== false && this.hideEditingButtons === false) {
        const createButton = {
          title: this.$i18n_t('Core.ListViewPage.createButton.label'),
          color: this.color,
          dataCy: 'create_button',
          icon: 'fal fa-plus',
          onClick: () => this.$emit('button:create'),
          href: this.createButtonHref
        };
        if (typeof createButton.onClick !== 'function' && !createButton.href) {
          // navigate to current path + /new
          createButton.href = this.$router.resolve({
            path: `${this.$route.path}/new`
          }).href;
        }
        buttons.push(createButton);
      }
      return buttons;
    },
    actualItems() {
      if (typeof this.fetchItems === 'function') {
        return this.items;
      }
      return this.staticItems;
    },
    isEmptyList() {
      return this.totalItemsCount === 0;
    }
  },
  watch: {
    tablePaginationOptions: {
      handler: function (newVal, oldVal) {
        if (this.isSettingInitialPagination) {
          return;
        }
        // store new pagination values in the route
        this.updateRouterParams({
          page: newVal.page,
          perPage: newVal.itemsPerPage,
          sortBy: newVal.sortBy[0],
          sortDirection: newVal.sortDesc[0] ? 'desc' : 'asc'
        });
        // store new items-per-page value in the store
        if (newVal.itemsPerPage !== oldVal.itemsPerPage) {
          const data = {
            id: this.sessionUserId,
            properties: {
              userDefaults: {
                [`${this.listType}ListItemsPerPage`]: newVal.itemsPerPage
              }
            }
          };
          ApiService.updateUserPartial(data);
          this.$store.commit('app/setRowsPerPage', newVal.itemsPerPage);
        }

        // fetch new items
        this.callFetchItems();
      },
      deep: true
    },
    textSearchValue: {
      handler: function (newVal) {
        this.updateRouterParams({
          search: newVal
        });
        this.$emit('update:search', newVal);
        this.callFetchItems();
      }
    },
    externalFetchParams: {
      handler: function () {
        this.callFetchItems();
      },
      deep: true
    },
    totalItemsCount: {
      handler: function (newVal) {
        this.$emit('update:total-count', newVal);
      },
      immediate: true
    },
    staticItems: {
      handler: function (newVal) {
        if (typeof this.fetchItems === 'function') {
          return;
        }
        this.totalItemsCount = newVal.length;
      },
      immediate: true,
      deep: true
    }
  },
  async created() {
    if (!Array.isArray(this.tableHeaders) || this.tableHeaders.length === 0) {
      console.warn('tableHeaders is not defined or empty');
      return;
    }
    const user = await ApiService.getUser(this, this.$store.state.authentication.user.id);
    const userDefaults = get(user, 'properties.userDefaults', {}) || {};
    const page = parseInt(this.$route.query.page, 10) || DEFAULT_TABLE_PAGINATION_OPTIONS.page;
    const itemsPerPage = parseInt(userDefaults[`${this.listType}ListItemsPerPage`]) ||
    // per vuex store
    parseInt(this.$route.query.perPage, 10) ||
    // per query param
    DEFAULT_TABLE_PAGINATION_OPTIONS.itemsPerPage; // per default
    const sortBy = this.$route.query.sortBy || DEFAULT_TABLE_PAGINATION_OPTIONS.sortBy[0];
    const sortDesc = this.$route.query.sortDirection || (DEFAULT_TABLE_PAGINATION_OPTIONS.sortDesc[0] ? 'desc' : 'asc');
    this.tablePaginationOptions = {
      ...this.tablePaginationOptions,
      page,
      itemsPerPage,
      sortBy: [sortBy],
      sortDesc: [sortDesc === 'desc']
    };
    this.isSettingInitialPagination = true;
    this.$nextTick(() => {
      this.isSettingInitialPagination = false;
    });
    const textSearchValueFromQuery = this.$route.query.search || '';
    if (textSearchValueFromQuery) {
      this.textSearchValue = textSearchValueFromQuery;
    }
    this.callFetchItems();
  },
  methods: {
    async callDeleteItem() {
      try {
        this.isDeleting = true;
        if (typeof this.deleteItem !== 'function') {
          console.warn('deleteItem function is not defined');
          return;
        }
        await this.deleteItem(this.itemToBeDeleted);
        this.itemToBeDeleted = null;
        if (this.deleteItemSnackMessage) {
          this.$snack(this.deleteItemSnackMessage, 'success');
        } else {
          this.$snack(this.$i18n_t('Core.ListViewPage.deleteItem.success.snackMessage'), 'success');
        }
        this.showDeletePrompt = false;
        this.callFetchItems();
      } catch (error) {
        this.itemToBeDeleted = null;
        this.deleteItemError = error;
        if (this.deleteItemErrorSnackMessage) {
          if (typeof this.deleteItemErrorSnackMessage === 'function') {
            this.$snack(this.deleteItemErrorSnackMessage(error), 'error');
          } else {
            this.$snack(this.deleteItemErrorSnackMessage, 'error');
          }
        } else {
          this.$snack(this.$i18n_t('Core.ListViewPage.deleteItem.error.snackMessage'), 'error');
        }
      }
    },
    onClickDeleteItem(item) {
      this.showDeletePrompt = true;
      this.itemToBeDeleted = item;
    },
    async updateRouterParams(params) {
      try {
        await this.$router.replace({
          query: {
            ...this.$route.query,
            ...params
          }
        });
      } catch (error) {
        console.error(error);
      }
    },
    callFetchItems() {
      this.isFetching = true;
      this.callFetchItemsDebounced();
    },
    callFetchItemsDebounced: debounce(async function () {
      this.isFetching = true;
      try {
        if (typeof this.fetchItems !== 'function') {
          return;
        }
        const {
          items,
          total
        } = await this.fetchItems({
          search: this.textSearchValue,
          page: this.tablePaginationOptions.page,
          perPage: this.tablePaginationOptions.itemsPerPage,
          sortBy: this.tablePaginationOptions.sortBy[0],
          sortDirection: this.tablePaginationOptions.sortDesc[0] ? 'desc' : 'asc',
          ...this.externalFetchParams
        });
        this.items = items;
        this.totalItemsCount = total;
      } catch (error) {
        console.error(error);
        this.$snack(this.$i18n_t(this.fetchErrorMessage), 'error');
      } finally {
        this.isFetching = false;
      }
    }, FETCH_LIST_DEBOUNCE_TIME_MS),
    getValueByPath(obj, path) {
      return get(obj, path);
    },
    getItemHref(item) {
      return typeof this.rowHref === 'function' ? this.rowHref(item) : '#';
    },
    navigateToRunner(deviceSyncStatus, hasLinkedVirtualDevices) {
      if (hasLinkedVirtualDevices === false) {
        return;
      }
      if (deviceSyncStatus.isRunning || !deviceSyncStatus.isRunning && deviceSyncStatus.syncFailed === true) {
        this.$router.push(`/background-jobs/?tab=bg-jobs-edit&openRunnerId=${deviceSyncStatus.backgroundRunnerId}`);
      }
    }
  }
};